import { useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import UsageTracker from '../../utils/UsageTracker';
import { RouterLink } from '../AcademyLinks';
import { WIZARD_URL_FRAGMENT } from '../../constants/Navigation';
import RecommendationsResetModal from './RecommendationsResetModal';
import { isLessonRecommendation, usePostRecommendationsFeedback, useRecommendations } from '../../features/recommendations';
import { CardsSection } from '../../features/cardsSection/components/CardsSection';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import getLang from 'academy-common-lib/utils/getLang';
import uniq from 'hs-lodash/uniq';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SubTitleTopMessage = ({
  showIndividualReasons,
  interactionMetadata
}) => {
  // retrieves hydrated card models for the interaction item to display
  const {
    cardModels
  } = useLearningContentCardModels({
    items: [{
      id: (interactionMetadata === null || interactionMetadata === void 0 ? void 0 : interactionMetadata.entityId) || 0,
      type: interactionMetadata === null || interactionMetadata === void 0 ? void 0 : interactionMetadata.entityType,
      language: getLang()
    }],
    skip: showIndividualReasons || !interactionMetadata
  });
  if (showIndividualReasons) return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.tracks.subheaders.personalRecommendations"
  });
  if (cardModels !== null && cardModels !== void 0 && cardModels.length) {
    var _cardModels$;
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "learningCenter.tracks.subheaders.becauseYouInteractedWithOne",
      options: {
        contentNames: cardModels === null || cardModels === void 0 || (_cardModels$ = cardModels[0]) === null || _cardModels$ === void 0 ? void 0 : _cardModels$.name
      }
    });
  }
  return null;
};
const SubTitle = ({
  showIndividualReasons,
  interactionMetadata,
  onInfoButtonClick
}) => {
  const handleResetLinkClick = () => {
    UsageTracker.track('interaction', {
      action: 'catalog-recommendation-reset-click'
    });
  };
  const handleResetInfoClick = () => {
    UsageTracker.track('interaction', {
      action: 'catalog-recommendation-reset-info-click'
    });
    onInfoButtonClick();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SubTitleTopMessage, {
      interactionMetadata: interactionMetadata,
      showIndividualReasons: showIndividualReasons
    }), ' ', /*#__PURE__*/_jsx(RouterLink, {
      onClick: handleResetLinkClick,
      to: WIZARD_URL_FRAGMENT,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.tracks.resetLink.linkMessage"
      })
    }), /*#__PURE__*/_jsx(IconButton, {
      onClick: handleResetInfoClick,
      size: "xs",
      use: "transparent",
      children: /*#__PURE__*/_jsx(InfoIcon, {
        color: "icon-core-subtle"
      })
    })]
  });
};
const useRecommendationsBasedOnProgressContent = () => {
  const {
    recommendations: recommendationsResponse,
    recommendationsLoading,
    recommendationsError
  } = useRecommendations();
  const {
    recommendations,
    correlationId
  } = recommendationsResponse || {};

  // retrieves hydrated card models for the elements to render
  const {
    cardModels,
    cardModelsError,
    cardModelsLoading
  } = useLearningContentCardModels({
    items: (recommendations === null || recommendations === void 0 ? void 0 : recommendations.map(({
      recommendationId,
      recommendationType,
      recommendationMetadata,
      recommendationStrategy
    }) => {
      return {
        id: recommendationId,
        type: recommendationType,
        language: getLang(),
        metadata: recommendationMetadata,
        strategy: recommendationStrategy
      };
    })) || [],
    skip: !(recommendations !== null && recommendations !== void 0 && recommendations.length)
  });
  const hasMultipleStrategies = useMemo(() => uniq(recommendations === null || recommendations === void 0 ? void 0 : recommendations.map(x => x.recommendationStrategy)).length > 1, [recommendations]);
  const hasMultipleUserInteractionIds = useMemo(() => uniq(cardModels === null || cardModels === void 0 ? void 0 : cardModels.map(x => {
    var _x$metadata;
    return (_x$metadata = x.metadata) === null || _x$metadata === void 0 ? void 0 : _x$metadata.entityId;
  })).length > 1, [cardModels]);
  return {
    content: cardModels,
    loading: recommendationsLoading || cardModelsLoading,
    error: recommendationsError || cardModelsError,
    correlationId,
    hasMultipleStrategies,
    hasMultipleUserInteractionIds
  };
};
const RecommendationsBasedOnProgress = () => {
  var _content$;
  const {
    postRecommendationsFeedback
  } = usePostRecommendationsFeedback();
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const {
    content,
    error,
    loading,
    correlationId,
    hasMultipleStrategies,
    hasMultipleUserInteractionIds
  } = useRecommendationsBasedOnProgressContent();
  if (!loading && !error && !(content !== null && content !== void 0 && content.length)) return null;
  const showIndividualReasons = hasMultipleStrategies || hasMultipleUserInteractionIds;
  const handleResetModalClose = () => {
    setIsResetModalOpen(false);
  };
  const handleInfoButtonClick = () => {
    setIsResetModalOpen(true);
  };
  const handleOnItemClick = ({
    id,
    type,
    language,
    name,
    strategy,
    metadata
  }, index) => {
    UsageTracker.track('interaction', {
      action: 'catalog-recommendation-card-click',
      cardNumber: index + 1,
      contentId: id,
      contentType: type,
      name,
      language: language,
      recommendationType: 'spark-based',
      recommendationStrategy: strategy,
      recommendedBecauseTrackId: (metadata === null || metadata === void 0 ? void 0 : metadata.entityType) === 'TRACK' && (metadata === null || metadata === void 0 ? void 0 : metadata.entityId),
      recommendedBecauseLessonId: (metadata === null || metadata === void 0 ? void 0 : metadata.entityType) && isLessonRecommendation(metadata === null || metadata === void 0 ? void 0 : metadata.entityType) && (metadata === null || metadata === void 0 ? void 0 : metadata.entityId)
    });
    if (correlationId) {
      postRecommendationsFeedback({
        correlationId,
        entityType: type,
        entityId: id
      }).catch(Raven.captureException.bind(Raven));
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardsSection, {
      appearsIn: "recommendations-progress",
      content: content,
      description: /*#__PURE__*/_jsx(SubTitle, {
        interactionMetadata: content === null || content === void 0 || (_content$ = content[0]) === null || _content$ === void 0 ? void 0 : _content$.metadata,
        onInfoButtonClick: handleInfoButtonClick,
        showIndividualReasons: !!showIndividualReasons
      }),
      displayFullCardsOnMobile: false,
      error: error,
      header: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.tracks.headers.recommended"
      }),
      isLoading: loading,
      navMarker: !loading && !error ? 'HOMEPAGE_RECOMMENDATIONS_LOADED' : undefined,
      onItemClick: handleOnItemClick,
      size: "extra-large"
    }), isResetModalOpen && /*#__PURE__*/_jsx(RecommendationsResetModal, {
      onClose: handleResetModalClose
    })]
  });
};
export default RecommendationsBasedOnProgress;